// frontend/src/pages/driver/DriverDeliveries.jsx
import { useState, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import axios from 'axios';

const API = import.meta.env.VITE_API_URL || 'http://localhost:5000/api/v1';

const DriverDeliveries = () => {
  const [searchParams] = useSearchParams();
  const [loading, setLoading] = useState(true);
  const [deliveries, setDeliveries] = useState([]);
  const [selectedDelivery, setSelectedDelivery] = useState(null);
  const [showDetail, setShowDetail] = useState(false);
  const [showStatusModal, setShowStatusModal] = useState(false);
  const [showPaymentModal, setShowPaymentModal] = useState(false);
  const [statusFilter, setStatusFilter] = useState(searchParams.get('status') || 'active');
  const [dateFilter, setDateFilter] = useState('today');
  const [searchQuery, setSearchQuery] = useState('');
  const [error, setError] = useState('');

   useEffect(() => {
    fetchDeliveries();
  }, [statusFilter, dateFilter, searchQuery]);

  const getDateRange = () => {
    const now = new Date();
    let startDate = new Date();
    let endDate = new Date();

    switch (dateFilter) {
      case 'today':
        startDate.setHours(0, 0, 0, 0);
        endDate = new Date();
        break;
      case 'yesterday':
        startDate.setDate(now.getDate() - 1);
        startDate.setHours(0, 0, 0, 0);
        endDate = new Date(startDate);
        endDate.setHours(23, 59, 59, 999);
        break;
      case 'week':
        startDate.setDate(now.getDate() - 7);
        startDate.setHours(0, 0, 0, 0);
        endDate = new Date();
        break;
      case 'month':
        startDate.setDate(now.getDate() - 30);
        startDate.setHours(0, 0, 0, 0);
        endDate = new Date();
        break;
      case 'all':
      default:
        return {};
    }

    return {
      date_from: startDate.toISOString().split('T')[0],
      date_to: endDate.toISOString().split('T')[0]
    };
  };

  const fetchDeliveries = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('token');
      const dateRange = getDateRange();
      
      const response = await axios.get(`${API}/driver/deliveries`, {
        headers: { Authorization: `Bearer ${token}` },
        params: { 
          status: statusFilter, 
          ...dateRange,
          search: searchQuery || undefined
        }
      });
      
      if (response.data.success) {
        setDeliveries(response.data.data);
      }
    } catch (err) {
      console.error('Deliveries error:', err);
      setError(err.response?.data?.message || 'Хүргэлтүүд татахад алдаа гарлаа');
    } finally {
      setLoading(false);
    }
  };

  const fetchDeliveryDetail = async (id) => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/driver/deliveries/${id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      if (response.data.success) {
        setSelectedDelivery(response.data.data);
        setShowDetail(true);
      }
    } catch (err) {
      console.error('Detail error:', err);
      alert(err.response?.data?.message || 'Мэдээлэл татахад алдаа гарлаа');
    }
  };

  const getStatusBadge = (status) => {
    const styles = {
      pending: 'bg-gray-100 text-gray-700',
      in_transit: 'bg-orange-100 text-orange-700',
      delivered: 'bg-green-100 text-green-700',
      postponed: 'bg-yellow-100 text-yellow-700',
      cancelled: 'bg-red-100 text-red-700'
    };
    const labels = {
      pending: 'Хүлээгдэж буй',
      in_transit: 'Хүргэж байна',
      delivered: 'Хүргэсэн',
      postponed: 'Хойшилсон',
      cancelled: 'Цуцалсан'
    };
    return (
      <span className={`px-2 py-1 rounded-full text-xs font-medium ${styles[status] || styles.pending}`}>
        {labels[status] || status}
      </span>
    );
  };

  const getPaymentBadge = (status) => {
    const styles = {
      unpaid: 'bg-red-100 text-red-700',
      paid_by_driver: 'bg-blue-100 text-blue-700',
      paid_by_company: 'bg-purple-100 text-purple-700',
      prepaid: 'bg-green-100 text-green-700'
    };
    const labels = {
      unpaid: 'Төлөгдөөгүй',
      paid_by_driver: 'Жолооч авсан',
      paid_by_company: 'Компани авсан',
      prepaid: 'Урьдчилж төлсөн'
    };
    return (
      <span className={`px-2 py-1 rounded-full text-xs font-medium ${styles[status] || styles.unpaid}`}>
        {labels[status] || status}
      </span>
    );
  };

  const getDateLabel = () => {
    const labels = {
      today: 'Өнөөдөр',
      yesterday: 'Өчигдөр',
      week: '7 хоног',
      month: '30 хоног',
      all: 'Бүх хугацаа'
    };
    return labels[dateFilter] || 'Өнөөдөр';
  };

  const getStatusLabel = () => {
    const labels = {
      active: 'Идэвхтэй',
      delivered: 'Хүргэсэн',
      postponed: 'Хойшилсон',
      cancelled: 'Цуцалсан',
      all: 'Бүгд'
    };
    return labels[statusFilter] || 'Идэвхтэй';
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[60vh]">
        <div className="text-center">
          <div className="w-12 h-12 border-4 border-blue-500 border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
          <p className="text-gray-500">Уншиж байна...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="p-4 space-y-4">
       {/* Header with Filters */}
        <div className="bg-white rounded-2xl p-4 shadow-sm border border-gray-100">
          <div className="flex items-center justify-between mb-4">
            <h1 className="text-lg font-bold text-gray-800">📦 Хүргэлтүүд</h1>
            <span className="text-sm text-gray-500 bg-gray-100 px-3 py-1 rounded-full">
              {deliveries.length} хүргэлт
            </span>
          </div>
          
          {/* Search Field */}
          <div className="mb-4">
            <div className="relative">
              <input
                type="text"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                placeholder="🔍 Дугаар, утас, нэрээр хайх..."
                className="w-full px-4 py-3 pl-10 bg-gray-50 border border-gray-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              />
              <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400">
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                </svg>
              </span>
              {searchQuery && (
                <button
                  onClick={() => setSearchQuery('')}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
                >
                  ✕
                </button>
              )}
            </div>
          </div>

          {/* Dropdown Filters */}
          <div className="grid grid-cols-2 gap-3">
          {/* Date Filter */}
          <div>
            <label className="block text-xs font-medium text-gray-500 mb-1">📅 Хугацаа</label>
            <select
              value={dateFilter}
              onChange={(e) => setDateFilter(e.target.value)}
              className="w-full px-3 py-2.5 bg-gray-50 border border-gray-200 rounded-xl text-sm font-medium text-gray-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent appearance-none cursor-pointer"
              style={{ backgroundImage: `url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' fill='none' viewBox='0 0 24 24' stroke='%236B7280'%3E%3Cpath stroke-linecap='round' stroke-linejoin='round' stroke-width='2' d='M19 9l-7 7-7-7'%3E%3C/path%3E%3C/svg%3E")`, backgroundRepeat: 'no-repeat', backgroundPosition: 'right 0.75rem center', backgroundSize: '1rem' }}
            >
              <option value="today">📅 Өнөөдөр</option>
              <option value="yesterday">⏪ Өчигдөр</option>
              <option value="week">📆 7 хоног</option>
              <option value="month">🗓️ 30 хоног</option>
              <option value="all">📋 Бүх хугацаа</option>
            </select>
          </div>
          
          {/* Status Filter */}
          <div>
            <label className="block text-xs font-medium text-gray-500 mb-1">🏷️ Төлөв</label>
            <select
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
              className="w-full px-3 py-2.5 bg-gray-50 border border-gray-200 rounded-xl text-sm font-medium text-gray-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent appearance-none cursor-pointer"
              style={{ backgroundImage: `url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' fill='none' viewBox='0 0 24 24' stroke='%236B7280'%3E%3Cpath stroke-linecap='round' stroke-linejoin='round' stroke-width='2' d='M19 9l-7 7-7-7'%3E%3C/path%3E%3C/svg%3E")`, backgroundRepeat: 'no-repeat', backgroundPosition: 'right 0.75rem center', backgroundSize: '1rem' }}
            >
              <option value="active">🚚 Идэвхтэй</option>
              <option value="delivered">✅ Хүргэсэн</option>
              <option value="postponed">⏸️ Хойшилсон</option>
              <option value="cancelled">❌ Цуцалсан</option>
              <option value="all">📋 Бүгд</option>
            </select>
          </div>
        </div>
        
        {/* Active Filter Tags */}
        <div className="flex flex-wrap gap-2 mt-3 pt-3 border-t border-gray-100">
          <span className="inline-flex items-center gap-1 px-2 py-1 bg-blue-50 text-blue-700 rounded-lg text-xs font-medium">
            📅 {getDateLabel()}
          </span>
          <span className="inline-flex items-center gap-1 px-2 py-1 bg-purple-50 text-purple-700 rounded-lg text-xs font-medium">
            🏷️ {getStatusLabel()}
          </span>
        </div>
      </div>

      {/* Error */}
      {error && (
        <div className="bg-red-50 border border-red-200 rounded-xl p-4 text-red-700">
          <p>{error}</p>
        </div>
      )}

      {/* Deliveries List */}
      {deliveries.length === 0 ? (
        <div className="bg-white rounded-2xl p-8 text-center shadow-sm border border-gray-100">
          <span className="text-6xl mb-4 block">📭</span>
          <p className="text-gray-500 font-medium">Хүргэлт олдсонгүй</p>
          <p className="text-gray-400 text-sm mt-1">Шүүлтүүрээ өөрчилж үзнэ үү</p>
        </div>
      ) : (
        <div className="space-y-3">
          {deliveries.map((delivery) => (
            <div
              key={delivery.id}
              onClick={() => fetchDeliveryDetail(delivery.id)}
              className="bg-white rounded-2xl p-4 shadow-sm border border-gray-100 active:bg-gray-50 cursor-pointer hover:shadow-md transition"
            >
              {/* Header */}
              <div className="flex items-start justify-between mb-3">
                <div>
                  <p className="font-bold text-gray-800">{delivery.delivery_number}</p>
                  <p className="text-sm text-gray-500">{delivery.shop?.shop_name}</p>
                </div>
                {getStatusBadge(delivery.status)}
              </div>

              {/* Customer Info */}
              <div className="space-y-2 mb-3">
                <div className="flex items-center gap-2 text-gray-700">
                  <span>👤</span>
                  <span className="font-medium">{delivery.customer_name}</span>
                </div>
                <div className="flex items-center gap-2 text-gray-600">
                  <span>📞</span>
                  <a href={`tel:${delivery.customer_phone}`} className="text-blue-600" onClick={(e) => e.stopPropagation()}>
                    {delivery.customer_phone}
                  </a>
                </div>
                <div className="flex items-start gap-2 text-gray-600">
                  <span>📍</span>
                  <span className="text-sm line-clamp-2">{delivery.address}</span>
                </div>
              </div>

              {/* Footer */}
              <div className="flex items-center justify-between pt-3 border-t border-gray-100">
                <div className="flex items-center gap-2">
                  <span className="text-lg">💰</span>
                  <div>
                    <span className="font-bold text-gray-800">
                      {((parseFloat(delivery.cod_amount) || 0) + (delivery.delivery_fee_from_customer ? (parseFloat(delivery.delivery_fee) || 0) : 0)).toLocaleString()}₮
                    </span>
                    {delivery.delivery_fee_from_customer && parseFloat(delivery.delivery_fee) > 0 && (
                      <span className="text-xs text-orange-600 ml-1">
                        (🚚+{parseFloat(delivery.delivery_fee).toLocaleString()})
                      </span>
                    )}
                  </div>
                </div>
                {getPaymentBadge(delivery.payment_status)}
              </div>

              {/* District/Zone */}
              {(delivery.district || delivery.zone) && (
                <div className="flex items-center gap-2 mt-2 pt-2 border-t border-gray-100">
                  {delivery.district && (
                    <span className="text-xs bg-gray-100 text-gray-600 px-2 py-1 rounded-full">
                      📍 {delivery.district.name}
                    </span>
                  )}
                  {delivery.zone && (
                    <span 
                      className="text-xs px-2 py-1 rounded-full"
                      style={{ 
                        backgroundColor: delivery.zone.color ? `${delivery.zone.color}20` : '#f3f4f6',
                        color: delivery.zone.color || '#6b7280'
                      }}
                    >
                      🗺️ {delivery.zone.zone_name}
                    </span>
                  )}
                </div>
              )}
            </div>
          ))}
        </div>
      )}

      {/* Detail Modal */}
      {showDetail && selectedDelivery && (
          <DeliveryDetailModal
            delivery={selectedDelivery}
            onClose={() => {
              setShowDetail(false);
              setSelectedDelivery(null);
            }}
            onStatusChange={() => {
              setShowStatusModal(true);
            }}
            onPaymentChange={() => {
              setShowPaymentModal(true);
            }}
            onRefresh={fetchDeliveries}
          />
        )}

       {/* Status Change Modal */}
        {showStatusModal && selectedDelivery && (
          <StatusChangeModal
            delivery={selectedDelivery}
            onClose={() => setShowStatusModal(false)}
            onSuccess={() => {
              setShowStatusModal(false);
              setShowDetail(false);
              fetchDeliveries();
            }}
          />
        )}

        {/* Payment Change Modal */}
        {showPaymentModal && selectedDelivery && (
          <PaymentChangeModal
            delivery={selectedDelivery}
            onClose={() => setShowPaymentModal(false)}
            onSuccess={() => {
              setShowPaymentModal(false);
              setShowDetail(false);
              fetchDeliveries();
            }}
          />
        )}
      </div>
    );
  };

// ============================================
// DELIVERY DETAIL MODAL
// ============================================
const DeliveryDetailModal = ({ delivery, onClose, onStatusChange, onPaymentChange, onRefresh }) => {
  return (
    <div className="fixed inset-0 bg-black/50 z-50 flex items-end sm:items-center justify-center">
      <div className="bg-white w-full sm:max-w-lg sm:rounded-2xl rounded-t-3xl max-h-[90vh] overflow-y-auto pb-20 sm:pb-0">
        {/* Header */}
        <div className="sticky top-0 bg-white border-b border-gray-200 p-4 flex items-center justify-between z-10">
          <div>
            <h2 className="font-bold text-lg">{delivery.delivery_number}</h2>
            <p className="text-sm text-gray-500">{delivery.shop?.shop_name}</p>
          </div>
          <button
            onClick={onClose}
            className="w-10 h-10 bg-gray-100 rounded-full flex items-center justify-center hover:bg-gray-200 transition"
          >
            ✕
          </button>
        </div>

        {/* Content */}
        <div className="p-4 space-y-4">
          {/* Customer */}
          <div className="bg-gray-50 rounded-xl p-4">
            <h3 className="font-semibold text-gray-800 mb-3">👤 Хүлээн авагч</h3>
            <div className="space-y-2">
              <p className="font-medium text-gray-800">{delivery.customer_name}</p>
              <a 
                href={`tel:${delivery.customer_phone}`}
                className="flex items-center gap-2 text-blue-600 font-medium"
              >
                <span className="w-8 h-8 bg-blue-100 rounded-full flex items-center justify-center">📞</span>
                {delivery.customer_phone}
              </a>
              <div className="flex items-start gap-2 text-gray-600">
                <span className="w-8 h-8 bg-gray-200 rounded-full flex items-center justify-center flex-shrink-0">📍</span>
                <div>
                  <p>{delivery.address}</p>
                  {delivery.address_detail && (
                    <p className="text-sm text-gray-500">{delivery.address_detail}</p>
                  )}
                </div>
              </div>
            </div>
          </div>

          {/* Delivery Instructions */}
          {delivery.delivery_instructions && (
            <div className="bg-yellow-50 rounded-xl p-4 border border-yellow-200">
              <h3 className="font-semibold text-yellow-800 mb-2">📝 Хүргэлтийн заавар</h3>
              <p className="text-yellow-700">{delivery.delivery_instructions}</p>
            </div>
          )}

         {/* Notes (Онлайн шопын тайлбар) */}
          {delivery.notes && (
            <div className="bg-blue-50 rounded-lg p-3 border border-blue-200">
              <p className="text-sm text-blue-700">
                <span className="font-medium">🏪 Тайлбар:</span> {delivery.notes}
              </p>
            </div>
          )}

          {/* Postpone/Cancel Reason */}
          {(delivery.postpone_reason || delivery.cancel_reason) && (
            <div className={`rounded-xl p-4 border ${
              delivery.status === 'cancelled' ? 'bg-red-50 border-red-200' : 'bg-orange-50 border-orange-200'
            }`}>
              <h3 className={`font-semibold mb-2 ${
                delivery.status === 'cancelled' ? 'text-red-800' : 'text-orange-800'
              }`}>
                {delivery.status === 'cancelled' ? '❌ Цуцалсан шалтгаан' : '⏸️ Хойшлуулсан шалтгаан'}
              </h3>
              <p className={`font-medium ${
                delivery.status === 'cancelled' ? 'text-red-700' : 'text-orange-700'
              }`}>
                {delivery.postpone_reason === 'customer_unavailable' && 'Хүлээн авагч байхгүй'}
                {delivery.postpone_reason === 'phone_unreachable' && 'Утас авахгүй'}
                {delivery.postpone_reason === 'address_not_found' && 'Хаяг олдохгүй'}
                {delivery.postpone_reason === 'reschedule_request' && 'Өөр цагт хүргэх хүсэлт'}
                {delivery.cancel_reason === 'customer_refused' && 'Хүлээн авагч татгалзсан'}
                {delivery.cancel_reason === 'wrong_address' && 'Хаяг буруу'}
                {delivery.cancel_reason === 'damaged_goods' && 'Бараа гэмтсэн'}
                {delivery.cancel_reason === 'order_cancelled' && 'Захиалга цуцлагдсан'}
                {(delivery.postpone_reason === 'other' || delivery.cancel_reason === 'other') && 'Бусад'}
              </p>
              {(delivery.postpone_reason_text || delivery.cancel_reason_text) && (
                <p className={`mt-2 text-sm ${
                  delivery.status === 'cancelled' ? 'text-red-600' : 'text-orange-600'
                }`}>
                  💬 {delivery.postpone_reason_text || delivery.cancel_reason_text}
                </p>
              )}
            </div>
          )}

          {/* Products */}
          {delivery.outbound?.items && delivery.outbound.items.length > 0 && (
            <div className="bg-gray-50 rounded-xl p-4">
              <h3 className="font-semibold text-gray-800 mb-3">📦 Бараа ({delivery.outbound.items.length})</h3>
              <div className="space-y-2">
                {delivery.outbound.items.map((item, idx) => (
                  <div key={idx} className="flex items-center justify-between bg-white p-3 rounded-lg">
                    <div className="flex items-center gap-3">
                      {item.product?.image_url ? (
                        <img src={item.product.image_url.startsWith('http') ? item.product.image_url : `http://localhost:5000${item.product.image_url}`} alt="" className="w-10 h-10 rounded-lg object-cover" />
                      ) : (
                        <div className="w-10 h-10 bg-gray-200 rounded-lg flex items-center justify-center">📦</div>
                      )}
                      <div>
                        <p className="font-medium text-gray-800 text-sm">{item.product?.product_name}</p>
                        {item.variant && (
                          <p className="text-xs text-gray-500">{item.variant.variant_name}</p>
                        )}
                      </div>
                    </div>
                    <div className="text-right">
                      <p className="font-bold text-gray-800">x{item.quantity}</p>
                      <p className="text-xs text-gray-500">{(item.unit_price || 0).toLocaleString()}₮</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

           {/* Payment */}
          <div className="bg-green-50 rounded-xl p-4 border border-green-200">
            <h3 className="font-semibold text-green-800 mb-3">💰 Төлбөр</h3>
            
             {/* Хэсэгчлэн төлсөн бол дэлгэрэнгүй харуулах */}
            {delivery.payment_status === 'partial' ? (
              <div className="space-y-2">
                <div className="flex items-center justify-between">
                  <span className="text-green-700">Нийт дүн:</span>
                  <span className="font-bold text-green-800">
                    {((parseFloat(delivery.cod_amount) || 0) + (delivery.delivery_fee_from_customer ? (parseFloat(delivery.delivery_fee) || 0) : 0)).toLocaleString()}₮
                  </span>
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-green-700">Төлсөн:</span>
                  <span className="font-bold text-blue-600">
                    {(delivery.partial_amount || 0).toLocaleString()}₮
                  </span>
                </div>
                   <div className="flex items-center justify-between pt-2 border-t border-green-200">
                  <span className="text-green-700 font-semibold">Үлдэгдэл:</span>
                  <span className="font-bold text-2xl text-red-600">
                    {(((parseFloat(delivery.cod_amount) || 0) + (delivery.delivery_fee_from_customer ? (parseFloat(delivery.delivery_fee) || 0) : 0)) - (parseFloat(delivery.partial_amount) || 0)).toLocaleString()}₮
                  </span>
                </div>
                {delivery.partial_note && (
                  <div className="mt-2 p-2 bg-yellow-50 rounded-lg border border-yellow-200">
                    <p className="text-xs text-yellow-700">💬 {delivery.partial_note}</p>
                  </div>
                )}
              </div>
            ) : (
              <div className="space-y-2">
                {/* COD дүн (барааны төлбөр) */}
                <div className="flex items-center justify-between">
                  <span className="text-green-700">COD дүн:</span>
                  <span className="font-bold text-green-800">
                    {(delivery.cod_amount || 0).toLocaleString()}₮
                  </span>
                </div>
                
                {/* Хүргэлтийн төлбөр (захиалагчаас авах бол) */}
                {delivery.delivery_fee_from_customer && delivery.delivery_fee > 0 && (
                  <div className="flex items-center justify-between">
                    <span className="text-orange-700">Хүргэлтийн төлбөр:</span>
                    <span className="font-bold text-orange-600">
                      +{(delivery.delivery_fee || 0).toLocaleString()}₮
                    </span>
                  </div>
                )}
                
                {/* Нийт авах дүн */}
                <div className="flex items-center justify-between pt-2 border-t border-green-200">
                  <span className="text-green-700 font-semibold">Нийт авах дүн:</span>
                  <span className="font-bold text-2xl text-green-800">
                    {((parseFloat(delivery.cod_amount) || 0) + (delivery.delivery_fee_from_customer ? (parseFloat(delivery.delivery_fee) || 0) : 0)).toLocaleString()}₮
                  </span>
                </div>
                
                {/* Тайлбар */}
                {delivery.delivery_fee_from_customer && delivery.delivery_fee > 0 && (
                  <div className="mt-2 p-2 bg-orange-50 rounded-lg border border-orange-200">
                    <p className="text-xs text-orange-700">
                      🚚 Хүргэлтийн төлбөр захиалагчаас авна
                    </p>
                  </div>
                )}
              </div>
            )}
          </div>
        </div>

        {/* Actions - Төлөв өөрчлөх */}
        {(delivery.status === 'in_transit' || delivery.status === 'pending' || delivery.status === 'postponed') && (
          <div className="sticky bottom-0 left-0 right-0 bg-white border-t border-gray-200 p-4 safe-area-bottom">
            <button
              onClick={onStatusChange}
              className="w-full py-4 bg-gradient-to-r from-blue-500 to-blue-600 text-white font-bold rounded-2xl shadow-lg active:scale-[0.98] transition text-center"
            >
              📝 Төлөв өөрчлөх
            </button>
          </div>
        )}

        {/* Actions - Төлбөрийн төлөв өөрчлөх (Хүргэгдсэн + төлөөгүй/partial үед) */}
        {delivery.status === 'delivered' && 
         (delivery.payment_status === 'unpaid' || delivery.payment_status === 'partial') && (
          <div className="sticky bottom-0 left-0 right-0 bg-white border-t border-gray-200 p-4 safe-area-bottom">
            <button
              onClick={onPaymentChange}
              className="w-full py-4 bg-gradient-to-r from-green-500 to-green-600 text-white font-bold rounded-2xl shadow-lg active:scale-[0.98] transition text-center"
            >
              💰 Төлбөрийн төлөв өөрчлөх
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

// ============================================
// STATUS CHANGE MODAL
// ============================================
const StatusChangeModal = ({ delivery, onClose, onSuccess }) => {
  const [status, setStatus] = useState('');
  const [paymentStatus, setPaymentStatus] = useState('');
  const [partialAmount, setPartialAmount] = useState('');
  const [partialNote, setPartialNote] = useState('');
  const [reason, setReason] = useState('');
  const [reasonText, setReasonText] = useState('');
  const [loading, setLoading] = useState(false);

  const postponeReasons = [
    { value: 'customer_unavailable', label: 'Хүлээн авагч байхгүй' },
    { value: 'phone_unreachable', label: 'Утас авахгүй' },
    { value: 'address_not_found', label: 'Хаяг олдохгүй' },
    { value: 'reschedule_request', label: 'Өөр цагт хүргэх хүсэлт' },
    { value: 'other', label: 'Бусад' }
  ];

  const cancelReasons = [
    { value: 'customer_refused', label: 'Хүлээн авагч татгалзсан' },
    { value: 'wrong_address', label: 'Хаяг буруу' },
    { value: 'damaged_goods', label: 'Бараа гэмтсэн' },
    { value: 'order_cancelled', label: 'Захиалга цуцлагдсан' },
    { value: 'other', label: 'Бусад' }
  ];

  const handleSubmit = async () => {
    if (!status) {
      alert('Төлөв сонгоно уу');
      return;
    }

    if (status === 'delivered' && !paymentStatus) {
      alert('Төлбөрийн төлөв сонгоно уу');
      return;
    }

    if ((status === 'postponed' || status === 'cancelled') && !reason) {
      alert('Шалтгаан сонгоно уу');
      return;
    }

    if ((status === 'postponed' || status === 'cancelled') && !reasonText.trim()) {
      alert('Тайлбар оруулна уу');
      return;
    }

    if (paymentStatus === 'partial' && (!partialAmount || parseFloat(partialAmount) <= 0)) {
      alert('Хэсэгчлэн төлсөн дүн оруулна уу');
      return;
    }

    try {
      setLoading(true);
      const token = localStorage.getItem('token');
      
      const data = {
        status,
        payment_status: status === 'delivered' ? paymentStatus : undefined,
        partial_amount: paymentStatus === 'partial' ? parseFloat(partialAmount) : undefined,
        partial_note: paymentStatus === 'partial' ? partialNote : undefined,
        reason: status !== 'delivered' ? reason : undefined,
        reason_text: status !== 'delivered' ? reasonText : undefined
      };

      await axios.put(
        `${API}/driver/deliveries/${delivery.id}/status`,
        data,
        { headers: { Authorization: `Bearer ${token}` } }
      );

      onSuccess();
    } catch (err) {
      console.error('Status update error:', err);
      alert(err.response?.data?.message || 'Төлөв өөрчлөхөд алдаа гарлаа');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/50 z-[60] flex items-end sm:items-center justify-center">
      <div className="bg-white w-full sm:max-w-md sm:rounded-2xl rounded-t-3xl max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="sticky top-0 bg-white border-b border-gray-200 p-4 flex items-center justify-between z-10">
          <div>
            <h2 className="font-bold text-lg">Төлөв өөрчлөх</h2>
            <p className="text-sm text-gray-500">{delivery.delivery_number}</p>
          </div>
          <button 
            onClick={onClose} 
            className="w-10 h-10 bg-gray-100 rounded-full flex items-center justify-center hover:bg-gray-200"
          >
            ✕
          </button>
        </div>

        {/* Content */}
        <div className="p-4 space-y-5">
          {/* Авах дүн */}
          <div className="bg-blue-50 rounded-2xl p-4">
            <p className="text-blue-600 text-sm">Авах дүн</p>
            <p className="font-bold text-2xl text-blue-800">
              {(delivery.cod_amount || 0).toLocaleString()}₮
            </p>
          </div>

          {/* Status Selection */}
          <div>
            <label className="block font-semibold text-gray-700 mb-3">Төлөв сонгох</label>
            <div className="space-y-3">
              <button
                onClick={() => { setStatus('delivered'); setReason(''); setReasonText(''); }}
                className={`w-full p-4 rounded-2xl border-2 text-left transition ${
                  status === 'delivered' ? 'border-green-500 bg-green-50' : 'border-gray-200 hover:border-gray-300'
                }`}
              >
                <div className="flex items-center gap-3">
                  <span className="text-3xl">✅</span>
                  <div>
                    <p className="font-bold text-gray-800">Хүргэсэн</p>
                    <p className="text-sm text-gray-500">Амжилттай хүргэгдсэн</p>
                  </div>
                </div>
              </button>

              <button
                onClick={() => { setStatus('postponed'); setPaymentStatus(''); }}
                className={`w-full p-4 rounded-2xl border-2 text-left transition ${
                  status === 'postponed' ? 'border-yellow-500 bg-yellow-50' : 'border-gray-200 hover:border-gray-300'
                }`}
              >
                <div className="flex items-center gap-3">
                  <span className="text-3xl">⏸️</span>
                  <div>
                    <p className="font-bold text-gray-800">Хойшлуулах</p>
                    <p className="text-sm text-gray-500">Дараа хүргэх</p>
                  </div>
                </div>
              </button>

              <button
                onClick={() => { setStatus('cancelled'); setPaymentStatus(''); }}
                className={`w-full p-4 rounded-2xl border-2 text-left transition ${
                  status === 'cancelled' ? 'border-red-500 bg-red-50' : 'border-gray-200 hover:border-gray-300'
                }`}
              >
                <div className="flex items-center gap-3">
                  <span className="text-3xl">❌</span>
                  <div>
                    <p className="font-bold text-gray-800">Цуцлах</p>
                    <p className="text-sm text-gray-500">Хүргэх боломжгүй</p>
                  </div>
                </div>
              </button>
            </div>
          </div>

          {/* Payment Status (for delivered) */}
          {status === 'delivered' && (
            <div>
              <label className="block font-semibold text-gray-700 mb-3">Төлбөрийн төлөв</label>
              <div className="space-y-3">
                {/* Төлөөгүй */}
                <button
                  onClick={() => setPaymentStatus('unpaid')}
                  className={`w-full p-4 rounded-2xl border-2 text-left transition ${
                    paymentStatus === 'unpaid' ? 'border-red-500 bg-red-50' : 'border-gray-200 hover:border-gray-300'
                  }`}
                >
                  <div className="flex items-center gap-3">
                    <span className="text-2xl">❌</span>
                    <div>
                      <p className="font-bold text-gray-800">Төлөөгүй</p>
                      <p className="text-sm text-gray-500">Захиалагч төлөөгүй</p>
                    </div>
                  </div>
                </button>

                {/* Би авсан */}
                <button
                  onClick={() => setPaymentStatus('paid_by_driver')}
                  className={`w-full p-4 rounded-2xl border-2 text-left transition ${
                    paymentStatus === 'paid_by_driver' ? 'border-blue-500 bg-blue-50' : 'border-gray-200 hover:border-gray-300'
                  }`}
                >
                  <div className="flex items-center gap-3">
                    <span className="text-2xl">💵</span>
                    <div>
                      <p className="font-bold text-gray-800">Би авсан</p>
                      <p className="text-sm text-gray-500">Бэлнээр авсан</p>
                    </div>
                  </div>
                </button>

                <button
                  onClick={() => setPaymentStatus('paid_by_company')}
                  className={`w-full p-4 rounded-2xl border-2 text-left transition ${
                    paymentStatus === 'paid_by_company' ? 'border-purple-500 bg-purple-50' : 'border-gray-200 hover:border-gray-300'
                  }`}
                >
                  <div className="flex items-center gap-3">
                    <span className="text-2xl">🏦</span>
                    <div>
                      <p className="font-bold text-gray-800">Компаний дансруу</p>
                      <p className="text-sm text-gray-500">Шилжүүлгээр төлсөн</p>
                    </div>
                  </div>
                </button>

                <button
                  onClick={() => setPaymentStatus('prepaid')}
                  className={`w-full p-4 rounded-2xl border-2 text-left transition ${
                    paymentStatus === 'prepaid' ? 'border-green-500 bg-green-50' : 'border-gray-200 hover:border-gray-300'
                  }`}
                >
                  <div className="flex items-center gap-3">
                    <span className="text-2xl">✅</span>
                    <div>
                      <p className="font-bold text-gray-800">Урьдчилж төлсөн</p>
                      <p className="text-sm text-gray-500">Авах дүн байхгүй</p>
                    </div>
                  </div>
                </button>

                <button
                  onClick={() => setPaymentStatus('partial')}
                  className={`w-full p-4 rounded-2xl border-2 text-left transition ${
                    paymentStatus === 'partial' ? 'border-orange-500 bg-orange-50' : 'border-gray-200 hover:border-gray-300'
                  }`}
                >
                  <div className="flex items-center gap-3">
                    <span className="text-2xl">➗</span>
                    <div>
                      <p className="font-bold text-gray-800">Хэсэгчлэн төлсөн</p>
                      <p className="text-sm text-gray-500">Зарим хэсгийг төлсөн</p>
                    </div>
                  </div>
                </button>

                {paymentStatus === 'partial' && (
                  <div className="mt-3 space-y-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">Авсан дүн (₮) *</label>
                      <input
                        type="number"
                        value={partialAmount}
                        onChange={(e) => setPartialAmount(e.target.value)}
                        placeholder="Дүн оруулах..."
                        className="w-full p-4 border-2 border-gray-200 rounded-2xl focus:border-orange-500 focus:outline-none text-lg font-bold"
                      />
                    </div>
                    
                    {/* Үлдэгдэл харуулах */}
                    <div className="bg-orange-50 rounded-xl p-3 border border-orange-200">
                      <div className="flex justify-between items-center">
                        <span className="text-orange-700">Төлөх дүн:</span>
                        <span className="font-bold text-orange-800">{(delivery.cod_amount || 0).toLocaleString()}₮</span>
                      </div>
                      <div className="flex justify-between items-center mt-1">
                        <span className="text-orange-700">Төлсөн:</span>
                        <span className="font-bold text-green-600">{(parseFloat(partialAmount) || 0).toLocaleString()}₮</span>
                      </div>
                      <div className="flex justify-between items-center mt-1 pt-2 border-t border-orange-200">
                        <span className="text-orange-700 font-semibold">Үлдэгдэл:</span>
                        <span className="font-bold text-red-600 text-lg">
                          {((delivery.cod_amount || 0) - (parseFloat(partialAmount) || 0)).toLocaleString()}₮
                        </span>
                      </div>
                    </div>
                    
                    {/* Тайлбар */}
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">Тайлбар (заавал биш)</label>
                      <textarea
                        value={partialNote}
                        onChange={(e) => setPartialNote(e.target.value)}
                        placeholder="Жишээ: Үлдэгдлийг маргааш шилжүүлнэ..."
                        className="w-full p-3 border-2 border-gray-200 rounded-xl focus:border-orange-500 focus:outline-none resize-none"
                        rows={2}
                      />
                      <p className="text-xs text-gray-500 mt-1">
                        Энэ тайлбар компани болон онлайн шопод харагдана
                      </p>
                    </div>
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Reason Selection (for postponed/cancelled) */}
          {(status === 'postponed' || status === 'cancelled') && (
            <div>
              <label className="block font-semibold text-gray-700 mb-3">Шалтгаан</label>
              <div className="space-y-2">
                {(status === 'postponed' ? postponeReasons : cancelReasons).map((r) => (
                  <button
                    key={r.value}
                    onClick={() => setReason(r.value)}
                    className={`w-full p-3 rounded-xl border-2 text-left transition ${
                      reason === r.value
                        ? status === 'postponed' ? 'border-yellow-500 bg-yellow-50' : 'border-red-500 bg-red-50'
                        : 'border-gray-200 hover:border-gray-300'
                    }`}
                  >
                    {r.label}
                  </button>
                ))}
              </div>

              <div className="mt-4">
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Тайлбар <span className="text-red-500">*</span>
                </label>
                <textarea
                  value={reasonText}
                  onChange={(e) => setReasonText(e.target.value)}
                  placeholder="Дэлгэрэнгүй тайлбар оруулна уу..."
                  className="w-full p-4 border-2 border-gray-200 rounded-2xl focus:border-blue-500 focus:outline-none resize-none"
                  rows={3}
                />
                <p className="text-xs text-gray-500 mt-1">
                  Энэ тайлбар компани болон онлайн шопод харагдана
                </p>
              </div>
            </div>
          )}
        </div>

        {/* Submit Button */}
        <div className="sticky bottom-0 bg-white border-t border-gray-200 p-4">
          <button
            onClick={handleSubmit}
            disabled={loading || !status}
            className={`w-full py-4 font-bold rounded-2xl transition ${
              loading || !status
                ? 'bg-gray-300 text-gray-500 cursor-not-allowed'
                : status === 'delivered'
                  ? 'bg-gradient-to-r from-green-500 to-green-600 text-white shadow-lg'
                  : status === 'postponed'
                    ? 'bg-gradient-to-r from-yellow-500 to-yellow-600 text-white shadow-lg'
                    : 'bg-gradient-to-r from-red-500 to-red-600 text-white shadow-lg'
            }`}
          >
            {loading ? (
              <span className="flex items-center justify-center gap-2">
                <svg className="animate-spin h-5 w-5" viewBox="0 0 24 24">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none" />
                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                </svg>
                Хадгалж байна...
              </span>
            ) : (
              status === 'delivered' ? '✅ Хүргэсэн болгох' :
              status === 'postponed' ? '⏸️ Хойшлуулах' :
              status === 'cancelled' ? '❌ Цуцлах' :
              'Хадгалах'
            )}
          </button>
        </div>
      </div>
    </div>
  );
};

// ============================================
// PAYMENT CHANGE MODAL (Төлбөрийн төлөв өөрчлөх)
// ============================================
const PaymentChangeModal = ({ delivery, onClose, onSuccess }) => {
  // Үлдэгдэл дүн тооцоолох
  const previousPaid = parseFloat(delivery.partial_amount) || 0;
  const totalAmount = parseFloat(delivery.cod_amount) || 0;
  const remainingToPay = totalAmount - previousPaid;

  const [paymentStatus, setPaymentStatus] = useState('');
  const [payAmount, setPayAmount] = useState('');
  const [partialNote, setPartialNote] = useState('');
  const [loading, setLoading] = useState(false);

  const handleSubmit = async () => {
    if (!paymentStatus) {
      alert('Төлбөрийн төлөв сонгоно у|у');
      return;
    }

    // Хэсэгчлэн төлсөн бол validation
    if (paymentStatus === 'partial') {
      const payAmountNum = parseFloat(payAmount) || 0;
      
      if (payAmountNum <= 0) {
        alert('Төлөх дүн оруулна уу');
        return;
      }
      
      if (payAmountNum > remainingToPay) {
        alert(`Төлөх дүн үлдэгдлээс (${remainingToPay.toLocaleString()}₮) их байж болохгүй`);
        return;
      }
    }

    try {
      setLoading(true);
      const token = localStorage.getItem('token');

      // Нийт төлсөн дүн = өмнө төлсөн + одоо төлж байгаа
      const newPartialAmount = paymentStatus === 'partial' 
        ? previousPaid + parseFloat(payAmount)
        : 0;

      await axios.put(
        `${API}/driver/deliveries/${delivery.id}/payment-status`,
        {
          payment_status: paymentStatus,
          partial_amount: paymentStatus === 'partial' ? newPartialAmount : undefined,
          partial_note: paymentStatus === 'partial' ? partialNote : undefined
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );

      onSuccess();
    } catch (err) {
      console.error('Payment status update error:', err);
      alert(err.response?.data?.message || 'Төлбөрийн төлөв өөрчлөхөд алдаа гарлаа');
    } finally {
      setLoading(false);
    }
  };

  // Одоо төлж байгаа дүнг оруулсны дараах үлдэгдэл
  const currentPayAmount = parseFloat(payAmount) || 0;
  const newRemaining = remainingToPay - currentPayAmount;

  return (
    <div className="fixed inset-0 bg-black/50 z-[60] flex items-end sm:items-center justify-center">
      <div className="bg-white w-full sm:max-w-md sm:rounded-2xl rounded-t-3xl max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="sticky top-0 bg-white border-b border-gray-200 p-4 flex items-center justify-between z-10">
          <div>
            <h2 className="font-bold text-lg">💰 Төлбөрийн төлөв өөрчлөх</h2>
            <p className="text-sm text-gray-500">{delivery.delivery_number}</p>
          </div>
          <button
            onClick={onClose}
            className="w-10 h-10 bg-gray-100 rounded-full flex items-center justify-center hover:bg-gray-200"
          >
            ✕
          </button>
        </div>

        {/* Content */}
        <div className="p-4 space-y-5">
          {/* Төлбөрийн мэдээлэл */}
          <div className="bg-blue-50 rounded-2xl p-4">
            <div className="flex justify-between items-center">
              <span className="text-blue-600">Нийт дүн:</span>
              <span className="font-bold text-xl text-blue-800">
                {totalAmount.toLocaleString()}₮
              </span>
            </div>
            {previousPaid > 0 && (
              <div className="flex justify-between items-center mt-2">
                <span className="text-blue-600">Өмнө төлсөн:</span>
                <span className="font-bold text-green-600">
                  {previousPaid.toLocaleString()}₮
                </span>
              </div>
            )}
            <div className="flex justify-between items-center mt-2 pt-2 border-t border-blue-200">
              <span className="text-blue-600 font-semibold">Төлөх үлдэгдэл:</span>
              <span className="font-bold text-2xl text-red-600">
                {remainingToPay.toLocaleString()}₮
              </span>
            </div>
          </div>

          {/* Payment Status Selection */}
          <div>
            <label className="block font-semibold text-gray-700 mb-3">Төлбөрийн төлөв</label>
            <div className="space-y-3">
              <button
                onClick={() => { setPaymentStatus('paid_by_driver'); setPayAmount(''); }}
                className={`w-full p-4 rounded-2xl border-2 text-left transition ${
                  paymentStatus === 'paid_by_driver' ? 'border-blue-500 bg-blue-50' : 'border-gray-200 hover:border-gray-300'
                }`}
              >
                <div className="flex items-center gap-3">
                  <span className="text-2xl">💵</span>
                  <div>
                    <p className="font-bold text-gray-800">Би авсан (Бүрэн)</p>
                    <p className="text-sm text-gray-500">Үлдэгдэл {remainingToPay.toLocaleString()}₮ бүрэн авсан</p>
                  </div>
                </div>
              </button>

              <button
                onClick={() => { setPaymentStatus('paid_by_company'); setPayAmount(''); }}
                className={`w-full p-4 rounded-2xl border-2 text-left transition ${
                  paymentStatus === 'paid_by_company' ? 'border-purple-500 bg-purple-50' : 'border-gray-200 hover:border-gray-300'
                }`}
              >
                <div className="flex items-center gap-3">
                  <span className="text-2xl">🏦</span>
                  <div>
                    <p className="font-bold text-gray-800">Компаний дансаар (Бүрэн)</p>
                    <p className="text-sm text-gray-500">Үлдэгдэл {remainingToPay.toLocaleString()}₮ шилжүүлсэн</p>
                  </div>
                </div>
              </button>

              <button
                onClick={() => setPaymentStatus('partial')}
                className={`w-full p-4 rounded-2xl border-2 text-left transition ${
                  paymentStatus === 'partial' ? 'border-orange-500 bg-orange-50' : 'border-gray-200 hover:border-gray-300'
                }`}
              >
                <div className="flex items-center gap-3">
                  <span className="text-2xl">➗</span>
                  <div>
                    <p className="font-bold text-gray-800">Хэсэгчлэн төлсөн</p>
                    <p className="text-sm text-gray-500">Үлдэгдлийн зарим хэсгийг төлсөн</p>
                  </div>
                </div>
              </button>
            </div>
          </div>

          {/* Partial Amount Input */}
          {paymentStatus === 'partial' && (
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Төлөх дүн (₮) *</label>
                <input
                  type="number"
                  value={payAmount}
                  onChange={(e) => setPayAmount(e.target.value)}
                  placeholder={`Хамгийн ихдээ ${remainingToPay.toLocaleString()}₮`}
                  max={remainingToPay}
                  className="w-full p-4 border-2 border-gray-200 rounded-2xl focus:border-orange-500 focus:outline-none text-lg font-bold"
                />
              </div>

              {/* Тооцоо харуулах */}
              <div className="bg-orange-50 rounded-xl p-3 border border-orange-200">
                <div className="flex justify-between items-center">
                  <span className="text-orange-700">Төлөх үлдэгдэл:</span>
                  <span className="font-bold text-orange-800">{remainingToPay.toLocaleString()}₮</span>
                </div>
                <div className="flex justify-between items-center mt-1">
                  <span className="text-orange-700">Одоо төлж байгаа:</span>
                  <span className="font-bold text-green-600">{currentPayAmount.toLocaleString()}₮</span>
                </div>
                <div className="flex justify-between items-center mt-1 pt-2 border-t border-orange-200">
                  <span className="text-orange-700 font-semibold">Шинэ үлдэгдэл:</span>
                  <span className={`font-bold text-lg ${newRemaining <= 0 ? 'text-green-600' : 'text-red-600'}`}>
                    {newRemaining <= 0 ? '0₮ ✅' : `${newRemaining.toLocaleString()}₮`}
                  </span>
                </div>
              </div>

              {/* Тайлбар */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Тайлбар (заавал биш)</label>
                <textarea
                  value={partialNote}
                  onChange={(e) => setPartialNote(e.target.value)}
                  placeholder="Жишээ: Үлдэгдлийг маргааш шилжүүлнэ..."
                  className="w-full p-3 border-2 border-gray-200 rounded-xl focus:border-orange-500 focus:outline-none resize-none"
                  rows={2}
                />
              </div>
            </div>
          )}
        </div>

        {/* Submit Button */}
        <div className="sticky bottom-0 bg-white border-t border-gray-200 p-4">
          <button
            onClick={handleSubmit}
            disabled={loading || !paymentStatus}
            className={`w-full py-4 font-bold rounded-2xl transition ${
              loading || !paymentStatus
                ? 'bg-gray-300 text-gray-500 cursor-not-allowed'
                : 'bg-gradient-to-r from-green-500 to-green-600 text-white shadow-lg'
            }`}
          >
            {loading ? (
              <span className="flex items-center justify-center gap-2">
                <svg className="animate-spin h-5 w-5" viewBox="0 0 24 24">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none" />
                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                </svg>
                Хадгалж байна...
              </span>
            ) : (
              '✅ Төлбөрийн төлөв хадгалах'
            )}
          </button>
        </div>
      </div>
    </div>
  );
};

export default DriverDeliveries;